<script setup>
import { Link, usePage } from '@inertiajs/vue3'
import { ref, computed } from 'vue'
import { Bell, GraduationCap, ChevronDown, Gauge, User, CircleHelp, LogOut, Menu, X, Settings, Moon, Sun, CreditCard, UserRoundCog, ShieldCheck } from 'lucide-vue-next'
import { Button } from '@/Components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/Components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/Components/ui/dropdown-menu'
import { Separator } from '@/Components/ui/separator'
import { Badge } from '@/Components/ui/badge'
import { useDarkMode } from '@/composables/useDarkMode'

import {
  home,
  library,
  becomeATutor,
  login,
  logout,
  tutors,
} from '@/routes/index'
import teacher from '@/routes/teacher'
import HamburgerIcon from "./icons/HamburgerIcon.vue";
import ImpersonateBanner from '@/Components/ImpersonateBanner.vue'
import ImpersonateModal from '@/Components/Modals/ImpersonateModal.vue'

const page = usePage()
const user = computed(() => page.props.auth?.user)
const isTeacher = computed(() => user.value?.user_type === 'teacher')
const isStudent = computed(() => ['student', 'customer'].includes(user.value?.user_type))
const isAdmin = computed(() => ['system', 'limited-system'].includes(user.value?.user_type))

const isMobileOpen = ref(false);
const showImpersonateModal = ref(false);
const impersonateEnabled = computed(() => page.props.impersonate?.enabled)

// Dashboard URL based on user type
const dashboardUrl = computed(() => isTeacher.value ? '/teacher/dashboard' : '/student/dashboard')

// Helper to check active route - simple matching for now
const isUrl = (path) => page.url.startsWith(path)

// Dark mode
const { isDark, toggleDarkMode } = useDarkMode()

// Current year for footer
const currentYear = new Date().getFullYear()
</script>

<template>
  <div class="min-h-screen bg-gray-50 font-sans text-gray-900 flex flex-col">
    <!-- Impersonate Banner -->
    <ImpersonateBanner />

    <!-- Header -->
    <header class="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div class="bg-white relative z-20 container mx-auto py-4 px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between">
          <!-- Logo & Nav -->
          <div class="flex items-center space-x-8">
            <Link :href="home()" class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <GraduationCap class="text-white w-6 h-6" />
              </div>
              <div>
                <h1 class="text-lg font-bold text-gray-900">Bindle</h1>
              </div>
            </Link>

            <nav class="hidden md:flex items-center space-x-1">
              <Link
                :href="library()"
                :class="['px-4 py-2 rounded-lg font-medium text-sm transition-colors', isUrl(library()) ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50']"
              >
                Content Library
              </Link>
              <Link
                :href="tutors()"
                :class="['px-4 py-2 rounded-lg font-medium text-sm transition-colors', isUrl(tutors()) ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50']"
              >
                Find a Tutor Plan
              </Link>
              <Link
                :href="becomeATutor()"
                :class="['px-4 py-2 rounded-lg font-medium text-sm transition-colors', isUrl(becomeATutor()) ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50']"
              >
                Become a Tutor
              </Link>
            </nav>
          </div>

          <!-- Right Actions -->
          <div class="flex items-center md:">
            <template v-if="user">
              <!-- Dashboard Button - Visible for logged in users -->
              <Link :href="dashboardUrl">
                <Button class="bg-linear-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium">
                  <Gauge class="w-4 h-4 md:`mr-2" />
                  <span class="hidden md:inline">Dashboard</span>
                </Button>
              </Link>

              <!-- Dark Mode Toggle -->
              <Button
                variant="ghost"
                size="icon"
                @click="toggleDarkMode"
                class="relative rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-50"
              >
                <Sun v-if="isDark" class="w-5 h-5" />
                <Moon v-else class="w-5 h-5" />
              </Button>

              <!-- Notifications -->
              <Button variant="ghost" size="icon" class="relative rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-50">
                <Bell class="w-5 h-5" />
                <span class="absolute top-1 right-2 w-2 h-2 bg-red-500 rounded-full"></span>
              </Button>

              <!-- User Dropdown -->
              <DropdownMenu>
                <DropdownMenuTrigger as-child>
                  <Button variant="ghost" class="flex items-center space-x-2 ml-2 hover:bg-transparent p-0">
                    <Avatar class="h-9 w-9 border-2 border-gray-200">
                      <AvatarImage :src="user.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=3B82F6&color=fff`" :alt="user.name" />
                      <AvatarFallback>{{ user.name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().substring(0, 2) }}</AvatarFallback>
                    </Avatar>
                    <ChevronDown class="text-gray-400 w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" class="w-56">
                  <!-- Teacher Menu Items -->
                  <template v-if="isTeacher">
                    <!-- Profile -->
                    <DropdownMenuItem as-child>
                      <Link :href="teacher.settings.personalInfo()" class="cursor-pointer w-full flex items-center">
                        <User class="mr-2 h-4 w-4" />
                        <span>Profile</span>
                      </Link>
                    </DropdownMenuItem>

                    <!-- Settings -->
                    <DropdownMenuItem as-child>
                      <Link :href="teacher.settings.subjects()" class="cursor-pointer w-full flex items-center">
                        <Settings class="mr-2 h-4 w-4" />
                        <span>Settings</span>
                      </Link>
                    </DropdownMenuItem>
                  </template>

                  <!-- Student Menu Items -->
                  <template v-if="isStudent">
                    <!-- Profile -->
                    <DropdownMenuItem as-child>
                      <Link href="/student/settings/profile" class="cursor-pointer w-full flex items-center">
                        <User class="mr-2 h-4 w-4" />
                        <span>Profile</span>
                      </Link>
                    </DropdownMenuItem>

                    <!-- Subscriptions -->
                    <DropdownMenuItem as-child>
                      <Link href="/student/subscriptions" class="cursor-pointer w-full flex items-center">
                        <CreditCard class="mr-2 h-4 w-4" />
                        <span>Subscriptions</span>
                      </Link>
                    </DropdownMenuItem>

                    <!-- Settings -->
                    <DropdownMenuItem as-child>
                      <Link href="/student/settings/notifications" class="cursor-pointer w-full flex items-center">
                        <Settings class="mr-2 h-4 w-4" />
                        <span>Settings</span>
                      </Link>
                    </DropdownMenuItem>
                  </template>

                  <!-- Admin Panel (system users only) -->
                  <template v-if="isAdmin">
                    <DropdownMenuSeparator />
                    <DropdownMenuItem as-child>
                      <a href="/admin" class="cursor-pointer w-full flex items-center text-purple-600 focus:text-purple-600">
                        <ShieldCheck class="mr-2 h-4 w-4" />
                        <span>Admin Panel</span>
                      </a>
                    </DropdownMenuItem>
                  </template>

                  <!-- Impersonate (dev only) -->
                  <template v-if="impersonateEnabled">
                    <DropdownMenuSeparator />
                    <DropdownMenuItem class="cursor-pointer w-full flex items-center text-amber-600 focus:text-amber-600" @click="showImpersonateModal = true">
                      <UserRoundCog class="mr-2 h-4 w-4" />
                      <span>Impersonate</span>
                    </DropdownMenuItem>
                  </template>

                  <DropdownMenuSeparator />

                  <!-- Help & Support -->
                  <DropdownMenuItem class="cursor-pointer w-full flex items-center">
                    <CircleHelp class="mr-2 h-4 w-4" />
                    <span>Help & Support</span>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  <!-- Sign Out -->
                  <DropdownMenuItem as-child>
                    <Link :href="logout()" method="post" as="button" class="w-full cursor-pointer flex items-center text-red-600 focus:text-red-600">
                      <LogOut class="mr-2 h-4 w-4" />
                      <span>Sign out</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </template>

            <template v-else>

              <!-- Dark Mode Toggle for guests -->
              <Button
                variant="ghost"
                size="icon"
                @click="toggleDarkMode"
                class="relative rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-50"
              >
                <Sun v-if="isDark" class="w-5 h-5" />
                <Moon v-else class="w-5 h-5" />
              </Button>

              <div class="md:hidden">
                  <HamburgerIcon
                      class="text-gray-400"
                      :is-open="isMobileOpen"
                      @click="isMobileOpen = !isMobileOpen"
                  />
              </div>

              <Link as="button" :href="login()" class="hidden md:inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer h-10 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white">
                    Login / Sign up
                </Link>

            </template>
          </div>
        </div>
      </div>
      <nav :class="{open: isMobileOpen}" class="mobile-menu absolute z-10 top-18 left-0 right-0 flex flex-col gap-4 p-4 pb-6 bg-white shadow-md">
        <Link
            :href="library()"
            :class="['px-4 py-2 rounded-lg font-medium text-sm transition-colors', isUrl(library()) ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50']"
        >
            Content Library
        </Link>
        <Link
            :href="tutors()"
            :class="['px-4 py-2 rounded-lg font-medium text-sm transition-colors', isUrl(tutors()) ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50']"
        >
            Find a Tutor Plan
        </Link>
        <Link
            :href="becomeATutor()"
            :class="['px-4 py-2 rounded-lg font-medium text-sm transition-colors', isUrl(becomeATutor()) ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50']"
        >
            Become a Tutor
        </Link>
        <Link as="button" :href="login()" class="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer h-10 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white">
            Login / Sign up
        </Link>
    </nav>
    </header>

    <!-- Main Content -->
    <main class="flex-1">
      <slot />
    </main>

    <!-- Footer -->
    <footer class="bg-white border-t border-gray-200 mt-16">
      <div class="max-w-7xl mx-auto px-8 py-12">
        <div class="flex flex-col md:flex-row justify-between gap-8 mb-8">
          <div class="max-w-md">
            <Link :href="home()" class="flex items-center space-x-2 mb-4">
              <div class="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-700 rounded-lg flex items-center justify-center">
                <GraduationCap class="text-white w-5 h-5" />
              </div>
              <span class="text-2xl font-bold text-gray-900">Bindle</span>
            </Link>
            <p class="text-gray-600 text-sm mb-4">Connecting students with expert tutors for personalized learning experiences.</p>
            <!-- <div class="flex items-center space-x-3">
              <a href="#" class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors text-gray-600">
                FB
              </a>
              <a href="#" class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors text-gray-600">
                TW
              </a>
              <a href="#" class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors text-gray-600">
                IG
              </a>
              <a href="#" class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors text-gray-600">
                IN
              </a>
            </div> -->
          </div>
          <div class="md:text-right">
            <h4 class="font-semibold text-gray-900 mb-4">Platform</h4>
            <ul class="space-y-2 text-sm text-gray-600">
              <li><Link :href="tutors()" class="hover:text-gray-900">Find Tutors</Link></li>
              <!-- Find Content hidden for now - revisit post launch -->
              <!-- <li><Link :href="library()" class="hover:text-gray-900">Find Content</Link></li> -->
              <li><Link :href="becomeATutor()" class="hover:text-gray-900">Become a Tutor</Link></li>
            </ul>
          </div>
          <!-- <div>
            <h4 class="font-semibold text-gray-900 mb-4">Support</h4>
            <ul class="space-y-2 text-sm text-gray-600">
              <li><a href="#" class="hover:text-gray-900">Help Center</a></li>
              <li><a href="#" class="hover:text-gray-900">Contact Us</a></li>
              <li><a href="#" class="hover:text-gray-900">FAQ</a></li>
              <li><a href="#" class="hover:text-gray-900">Community</a></li>
            </ul>
          </div> -->
          <!-- <div>
            <h4 class="font-semibold text-gray-900 mb-4">Company</h4>
            <ul class="space-y-2 text-sm text-gray-600">
              <li><a href="#" class="hover:text-gray-900">About</a></li>
              <li><a href="#" class="hover:text-gray-900">Careers</a></li>
              <li><a href="#" class="hover:text-gray-900">Blog</a></li>
              <li><a href="#" class="hover:text-gray-900">Press</a></li>
            </ul>
          </div> -->
        </div>
        <div class="pt-8 border-t border-gray-200 flex flex-col md:flex-row items-center justify-between text-sm text-gray-600">
          <p>© {{ currentYear }} Bindle. All rights reserved.</p>
          <div class="flex items-center space-x-6 mt-4 md:mt-0">
            <Link href="/privacy-policy" class="hover:text-gray-900">Privacy Policy</Link>
            <Link href="/terms-of-service" class="hover:text-gray-900">Tutor Terms</Link>
            <Link href="/student-terms" class="hover:text-gray-900">Student Terms</Link>
          </div>
        </div>
      </div>
    </footer>

    <!-- Impersonate Modal -->
    <ImpersonateModal :show="showImpersonateModal" @close="showImpersonateModal = false" />
  </div>
</template>


<style>
    nav.mobile-menu{
        @apply opacity-0 -translate-y-full transition-all duration-300 pointer-events-none;
        &.open{
            @apply translate-y-[0] opacity-100 pointer-events-auto;
        }
    }
</style>
